import { useAxios } from "../useAxios"
import { useAuthStore } from "../../../stores/auth/useAuthStore"

interface StreamEventPayload {
    type: string;
    data?: any;
}

interface StreamChatMessageParams {
    message: string;
    conversationId?: string;
    onEvent?: (event: StreamEventPayload) => void;
}

export const useAssistantRequest = () => {
    const { sendRequest, cancelRequest } = useAxios()
    const { store: { authData: { accessToken, refreshToken } } } = useAuthStore()

    const route = '/ai-assistant'

    const generateMessage = (payload: any) => {
        return sendRequest({
            slug: `ai-assistant`,
            url: `${route}/sparkle`,
            method: 'POST',
            payload: payload
        })
    };

    const sendChatMessage = (payload: { message: string, conversationId?: string }) => {
        return sendRequest({
            slug: 'ai-chat',
            url: `${route}/chat`,
            method: 'POST',
            payload: payload,
            displayToast: false
        })
    };

    const streamChatMessage = ({ message, conversationId, onEvent }: StreamChatMessageParams) => {
        const controller = new AbortController();
        const streamPromise = (async () => {
            const baseURL = process.env.REACT_APP_API_URL || '';
            const response = await fetch(`${baseURL}${route}/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'text/event-stream',
                    ...(accessToken && { Authorization: accessToken }),
                    ...(refreshToken && { 'refresh-token': refreshToken })
                },
                body: JSON.stringify({
                    message,
                    conversationId,
                    stream: true
                }),
                signal: controller.signal
            });

            if (!response.ok) {
                throw new Error('Failed to stream AI response.');
            }

            if (!response.body) {
                throw new Error('Streaming is not supported in this environment.');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let buffer = '';

            while (true) {
                const { value, done } = await reader.read();
                if (done) {
                    break;
                }

                buffer += decoder.decode(value, { stream: true });

                let boundaryIndex = buffer.indexOf('\n\n');
                while (boundaryIndex !== -1) {
                    const rawEvent = buffer.slice(0, boundaryIndex);
                    buffer = buffer.slice(boundaryIndex + 2);

                    rawEvent
                        .split('\n')
                        .map(line => line.trim())
                        .filter(line => line.startsWith('data:'))
                        .forEach(line => {
                            const payload = line.replace(/^data:\s*/, '');
                            if (!payload) {
                                return;
                            }

                            try {
                                const parsed: StreamEventPayload = JSON.parse(payload);
                                onEvent?.(parsed);
                            } catch (error) {
                                console.error('Failed to parse stream chunk:', error);
                            }
                        });

                    boundaryIndex = buffer.indexOf('\n\n');
                }
            }

            buffer += decoder.decode();

            if (buffer.trim().length > 0) {
                buffer
                    .split('\n')
                    .map(line => line.trim())
                    .filter(line => line.startsWith('data:'))
                    .forEach(line => {
                        const payload = line.replace(/^data:\s*/, '');
                        if (!payload) {
                            return;
                        }

                        try {
                            const parsed: StreamEventPayload = JSON.parse(payload);
                            onEvent?.(parsed);
                        } catch (error) {
                            console.error('Failed to parse trailing stream chunk:', error);
                        }
                    });
            }
        })();

        return {
            controller,
            streamPromise
        };
    };

    const getUserConversations = (params: { limit?: number, skip?: number } = {}) => {
        return sendRequest({
            slug: 'get-user-conversations',
            url: `${route}/conversations`,
            method: 'GET',
            params: params,
            displayToast: false
        })
    };

    const getConversation = (conversationId: string) => {
        return sendRequest({
            slug: 'get-conversation',
            url: `${route}/conversations/${conversationId}`,
            method: 'GET',
            displayToast: false
        })
    };

    const archiveConversation = (conversationId: string) => {
        return sendRequest({
            slug: 'archive-conversation',
            url: `${route}/conversations/${conversationId}/archive`,
            method: 'POST',
            displayToast: true
        })
    };

    const deleteConversation = (conversationId: string) => {
        return sendRequest({
            slug: 'delete-conversation',
            url: `${route}/conversations/${conversationId}`,
            method: 'DELETE',
            displayToast: true
        })
    };

    const cancelChat = () => {
        cancelRequest('ai-chat');
    };

    const generateAiRecommendations = (connectionId: string, all: boolean = false) => {
        // Important: slug must be per-connection so bulk runs don't abort each other
        // (useAxios aborts any in-flight request with the same slug).
        return sendRequest({
            slug: `generate-ai-recommendations:${connectionId}`,
            url: `${route}/recommendations/${connectionId}`,
            method: 'POST',
            displayToast: false,
            all
        });
    };

    const saveConversationArchive = (payload: { conversations: any[]; source?: string }) => {
        return sendRequest({
            slug: 'save-conversation-archive',
            url: `${route}/conversation-archive`,
            method: 'POST',
            payload,
            displayToast: false
        });
    };

    const recordTrainAiInboxModalSkip = () => {
        return sendRequest({
            slug: 'train-ai-inbox-modal-skip',
            url: `${route}/inbox/train-ai-modal-skip`,
            method: 'POST',
            displayToast: false
        });
    };

    const composeInboxNextMessage = (payload: {
        conversationId: string;
        messages: Array<{ sender: 'user' | 'peer'; text: string; sentAt?: string }>;
        topSimilar?: number;
        scanLimit?: number;
    }) => {
        return sendRequest({
            slug: 'compose-inbox-next',
            url: `${route}/inbox/compose-next`,
            method: 'POST',
            payload,
            displayToast: false
        });
    };

    const tagInboxConversation = (payload: {
        conversationId: string;
        messages: Array<{ sender: 'user' | 'peer'; text: string; sentAt?: string }>;
    }) => {
        return sendRequest({
            slug: 'tag-inbox-conversation',
            url: `${route}/inbox/conversation-tag`,
            method: 'POST',
            payload,
            displayToast: false
        });
    };

    return {
        generateMessage,
        sendChatMessage,
        streamChatMessage,
        getUserConversations,
        getConversation,
        archiveConversation,
        deleteConversation,
        cancelChat,
        generateAiRecommendations,
        saveConversationArchive,
        recordTrainAiInboxModalSkip,
        composeInboxNextMessage,
        tagInboxConversation
    };
}

